import React, { useState, useContext, useEffect, useRef } from "react";
import {
  Alert, Button, ButtonGroup, Card, CardBody,
  CardHeader, Col, Label, Row, Table
} from "reactstrap";
import DebounceInput from "react-debounce-input";
import NumberFormat from "react-number-format";
import { Link, useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import CreatableSelect from "react-select/creatable";
import Switch from "react-switch";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import _ from "lodash";
import { toast } from "react-toastify";
import { CustomerTypeahead, ConfirmModal } from ".";
import { DesktopContainer } from "../layouts";
import { api, dateHelpers, helpers, UserContext, constants } from "../utils";

const EMPTY_ROLE = {
  typeOfUserRole: null,
};

// The two save-blocking role rules, in precedence order. The server enforces both in validateUserModel.
const ROLE_GATE = {
  MIXED_ROLES: "mixedRoles",
  MISSING_CUSTOMER: "missingCustomer",
};

const defaultNewUser = {
  firstName: "",
  middleName: "",
  lastName: "",
  email: "",
  username: "",
  mobilePhoneNumber: "",
  officeNumber: "",
  faxNumber: "",
  timezone: constants.DEFAULT_TIMEZONE,
  state: "",
  postalCode: "",
  countryId: 0,
  password: "",
  companyId: 0,
  companyTitle: "",
  customerId: "",
  bioLink: "",
  twoFaRequired: true,
  typeOfMFA: constants.DUO_ID,
  roles: [],
  isProductionItemApprover: false,
};

export default function User(props) {
  const navigate = useNavigate();
  const userCtx = useContext(UserContext);
  const { id } = useParams();
  const [companyList, setCompanyList] = useState([]);
  const [countryList, setCountryList] = useState([]);
  // A customer-role user is assigned 1..5 accounts; one is the primary. Each entry:
  // { customerId, label ("ID | NAME"), isActive (GP), isPrimary }. selectedToAdd is the typeahead pick
  // waiting to be added.
  const [accounts, setAccounts] = useState([]);
  const [selectedToAdd, setSelectedToAdd] = useState(null);
  const [accountWarning, setAccountWarning] = useState(null); // inline nudge shown IN the accounts card
  const [defaultTimezone, setDefaultTimezone] = useState(null);
  const [message, setMessage] = useState(null);
  const [processApproverRoleList, setProcessApproverRoleList] = useState([]);
  const [roleSelectionState, setRoleSelectionState] = useState(null);
  const [timezoneList, setTimezoneList] = useState([]);
  const [user, setUser] = useState(defaultNewUser);
  const [confirm2FaOff, setConfirm2FaOff] = useState(false);
  const [roleGate, setRoleGate] = useState(null);
  const pendingPayload = useRef(null); // payload to save once the admin confirms a 2FA-off save
  const [mfaPickedByHand, setMfaPickedByHand] = useState(false); // stops a new user's method following their roles
  const [loadedTypeOfMFA, setLoadedTypeOfMFA] = useState(null); // the method as saved, not as edited
  const [confirmResetTotp, setConfirmResetTotp] = useState(false);
  const [resettingTotp, setResettingTotp] = useState(false);

  const isNewUser = !id || parseInt(id, 10) < 1;
  const currentUser = userCtx?.currentUser ? userCtx.currentUser : null;
  const anyCustomerRole = (roles) =>
    _.some(roles, (r) => _.includes(constants.CUSTOMER_ROLE_IDS, r.typeOfUserRole));
  // A customer-role user is scoped to a customer account, so the customer number is required for them and
  // stored for no one else. Drives both the typeahead's enabled state and the save gate.
  const hasCustomerRole = anyCustomerRole(user.roles);
  // Only Duo and the authenticator app are offered (texted codes were retired). A stored method outside them - none
  // set, or the retired texted codes - shows nothing selected, so with 2FA on the admin must pick one before saving;
  // the server refuses it too, and sign-in refuses such an account.
  const mfaOption = _.find(constants.MFA_TYPES, (x) => x.value === user.typeOfMFA) || null;
  // The status and reset describe the SAVED setup, so they need the account saved on the authenticator - and they
  // hide while the screen is edited away from it (another method, or 2FA off), where they would read as current.
  const showTotpStatus = !isNewUser
    && loadedTypeOfMFA === constants.TOTP_ID
    && user.twoFaRequired
    && user.typeOfMFA === constants.TOTP_ID;
  const hasInternalRole = _.some(
    user.roles,
    (r) => !_.includes(constants.CUSTOMER_ROLE_IDS, r.typeOfUserRole)
  );
  let newSelectedRoleContextDescription = null;

  // With no customer role the Add control is disabled, so clear the transient typeahead pick + nudge.
  // The assigned accounts themselves are NOT auto-cleared here: onRemoveRole blocks removing the LAST
  // customer role while accounts remain (the admin deletes them deliberately), and the accounts card
  // stays visible whenever accounts exist, so they can never be stranded behind a hidden card.
  useEffect(() => {
    if (!hasCustomerRole) {
      setSelectedToAdd(null);
      setAccountWarning(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasCustomerRole]);

  // Editing an existing user: load their assigned accounts with labels + GP active status (inactive ones
  // are shown, marked - the admin removes them deliberately). New users start with an empty list.
  useEffect(() => {
    if (isNewUser) return;
    api
      .fetch(`UserAdmin/AssignedCustomerAccounts/${id}`)
      .then((r) => {
        if (r.data?.success) {
          setAccounts(
            _.map(r.data.accounts, (a) => ({
              customerId: a.customerId,
              label: a.label,
              isActive: a.isActive,
              isPrimary: a.isPrimary,
            }))
          );
        }
      })
      .catch(api.catchHandler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    document.title = `Users`;
    let apiCalls = [];
    apiCalls.push(
      api
        .getTimezones()
        .then((list) => {
          return {
            timezones: _.chain(list)
              .map((tz) => {
                return {
                  value: tz,
                  label: tz,
                };
              })
              .sortBy((x) => {
                return x.label;
              })
              .value(),
          };
        })
        .catch(api.catchHandler)
    );
    apiCalls.push(
      api
        .getCountryList()
        .then((response) => {
          return {
            countries: helpers.idNameToValueLabel(response.countries),
          };
        })
        .catch(api.catchHandler)
    );
    apiCalls.push(
      api
        .fetch(`Reference/GetCompanyList`)
        .then((response) => {
          return {
            companies: helpers.idNameToValueLabel(response.data.companies),
          };
        })
        .catch(api.catchHandler)
    );
    apiCalls.push(
      api
        .fetch("Reference/GetProcessApproverRoleList")
        .then((response) => {
          return {
            processApproverRoleList: helpers.idNameToValueLabel(
              response.data.processApproverRoleList
            ),
          };
        })
        .catch(api.catchHandler)
    );
    if (parseInt(id, 10) > 0) {
      apiCalls.push(getUser());
    }
    Promise.all(apiCalls)
      .then((arrayResults) => {
        let aggResults = {};
        _.each(arrayResults, (x) => Object.assign(aggResults, x));
        setTimezoneList(aggResults.timezones ? aggResults.timezones : []);
        if (aggResults.timezones && aggResults.timezones.length) {
          setDefaultTimezone(
            _.find(
              aggResults.timezones,
              (t) => t.label === constants.DEFAULT_TIMEZONE
            )
          );
        }
        const approverTypeList = aggResults.processApproverRoleList
          ? aggResults.processApproverRoleList
          : [];
        setProcessApproverRoleList(approverTypeList);
        setCountryList(aggResults.countries ? aggResults.countries : []);
        setCompanyList(aggResults.companies ? aggResults.companies : []);
        let existingUser = aggResults.user ? aggResults.user : null;
        if (existingUser) {
          // The assigned-account list is loaded separately (with labels + active status) by the
          // AssignedCustomerAccounts effect; the raw customerId here is just the primary mirror.
          existingUser.processChangeApproverRoles = _.filter(
            approverTypeList,
            (r) => existingUser.approverRoleTypeIdList.includes(r.value)
          );
          setUser(existingUser);
          setLoadedTypeOfMFA(existingUser.typeOfMFA);
        }
      })
      .catch((error) =>
        setMessage({
          flavor: "alert-danger",
          text: "There was an error loading user data",
        })
      );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function appendRoleWithContent(roleList, newRole) {
    let builtNewRole = {
      ...EMPTY_ROLE,
      typeOfUserRole: newRole.typeSelection.value,
    };
    if (newRole.typeSelection.value === constants.ROLE_IDS.SYSADMIN) {
      roleList.push(builtNewRole);
      return roleList;
    }
    roleList.push(builtNewRole);
    return roleList;
  }

  function cleanseUserRoleDuplicates(roleList) {
    let stringArr = [];
    _.forEach(roleList, function (x) {
      if (x.roleName) {
        let roles = x.roleName.split(" ");
        stringArr.push(roles);
      }
    });

    let flatStringArr = _.flattenDeep(stringArr);

    let matches = flatStringArr.filter(
      (item, index) => index !== flatStringArr.indexOf(item)
    );

    let cleanList = _.reject(roleList, function (x) {
      if (x.roleName) {
        let roles = x.roleName.split(" ");
        return matches.includes(roles[0]) && roles[1] === "View";
      }
    });

    return cleanList;
  }

  function freshRoleState(typeSelection) {
    return {
      typeSelection: typeSelection,
    };
  }

  const getUser = () =>
    api
      .fetch(`UserAdmin/user/${id}`)
      .then((r) => ({
        user: r.data,
      }))
      .catch(api.catchHandler);

  function onAddRole() {
    if (!roleSelectionState?.newRole?.typeSelection) {
      setMessage({
        flavor: "projectWarning",
        text: "Select a role before attempting to add it to the list.",
      });
      return;
    }
    if (
      roleSelectionState?.newRole?.typeSelection.value ===
      constants.ROLE_IDS.SYSADMIN &&
      _.some(
        user.roles,
        (ur) => ur.typeOfUserRole === constants.ROLE_IDS.SYSADMIN
      )
    ) {
      setMessage({
        flavor: "projectWarning",
        text: "The system administrator role has already been given.",
      });
      return;
    }
    let duplicate = false;
    for (var j = 0; j < user.roles.length; j++) {
      const ur = user.roles[j];
      if (
        ur.typeOfUserRole === roleSelectionState.newRole.typeSelection.value
      ) {
        duplicate = true;
        break;
      }
    }
    if (duplicate) {
      setMessage({
        flavor: "projectWarning",
        text: "This role has already been given.",
      });
      return;
    }
    // Block a customer+internal mix at ENTRY rather than only at save: a user may hold customer roles OR
    // internal roles, never both. Reuses the same explanatory gate dialog as the save-time check (one
    // source of truth for the rule); the save gate + server stay as backstops.
    const addingCustomerRole = _.includes(
      constants.CUSTOMER_ROLE_IDS,
      roleSelectionState.newRole.typeSelection.value
    );
    if ((addingCustomerRole && hasInternalRole) || (!addingCustomerRole && hasCustomerRole)) {
      setRoleGate(ROLE_GATE.MIXED_ROLES);
      return;
    }
    setMessage(null);
    let newUser = Object.assign({}, user);
    newUser.roles = appendRoleWithContent(
      user.roles.slice(),
      roleSelectionState.newRole
    );
    setUser(withRoleDefaultMfa(newUser));
    setRoleSelectionState({
      ...roleSelectionState,
      newRole: freshRoleState(roleSelectionState.newRole.typeSelection),
    });
  }

  function onChange(fieldName, fieldValue) {
    let newUser = Object.assign({}, user);
    newUser[fieldName] = fieldValue;
    setUser(newUser);
  }

  function onCreateCompany(e) {
    if (!e) return;
    const trimmed = _.trim(e);
    if (!trimmed.length) return;
    api
      .post("Reference/SaveCompany", { id: 0, name: trimmed })
      .then((response) => {
        if (response.data.success) {
          let newList = companyList.slice();
          const newItem = {
            label: trimmed,
            value: parseInt(response.data.message, 10),
          };
          newList.unshift(newItem);
          setCompanyList(newList);
          onChange("companyId", newItem.value);
        } else {
          setMessage({ text: response?.data?.message, flavor: "alert-danger" });
        }
      })
      .catch(api.catchHandler);
  }

  const sameAccount = (a, b) =>
    !!a && !!b && String(a).trim().toUpperCase() === String(b).trim().toUpperCase();

  function onAddAccount() {
    // The typeahead only surfaces active customers, so a newly-added account is active. First one added is
    // the primary by default. A duplicate is refused with a message; the 5-account cap is also gated on
    // the Add control itself.
    if (!selectedToAdd?.value) return;
    if (accounts.length >= constants.MAX_CUSTOMER_ACCOUNTS) return;
    if (accounts.some((a) => sameAccount(a.customerId, selectedToAdd.value))) {
      setAccountWarning("That account is already assigned.");
      setSelectedToAdd(null);
      return;
    }
    setAccountWarning(null);
    setAccounts([
      ...accounts,
      {
        customerId: selectedToAdd.value,
        label: selectedToAdd.label,
        isActive: true,
        isPrimary: accounts.length === 0,
      },
    ]);
    setSelectedToAdd(null);
  }

  function onRemoveAccount(customerId) {
    const remaining = accounts.filter((a) => !sameAccount(a.customerId, customerId));
    // Removing the primary promotes the first remaining account, so there is always exactly one primary.
    if (remaining.length && !remaining.some((a) => a.isPrimary)) {
      remaining[0] = { ...remaining[0], isPrimary: true };
    }
    setAccounts(remaining);
  }

  function onSetPrimary(customerId) {
    setAccounts(accounts.map((a) => ({ ...a, isPrimary: sameAccount(a.customerId, customerId) })));
  }

  function onRemoveRole(selectedRole) {
    const newRoles = _.reject(
      user.roles,
      (r) =>
        r.typeOfUserRole === selectedRole.typeOfUserRole &&
        r.tenantId === selectedRole.tenantId
    );
    // Removing the LAST customer role hides the Customer Accounts card, so make the admin clear the
    // assigned accounts FIRST rather than leaving them stranded behind a hidden card (a customer->internal
    // demote also clears them server-side, so this makes that destructive step deliberate). Removing a
    // customer role while another remains is fine - the accounts are still valid.
    const removingCustomerRole = _.includes(
      constants.CUSTOMER_ROLE_IDS,
      selectedRole.typeOfUserRole
    );
    const stillHasCustomerRole = anyCustomerRole(newRoles);
    if (removingCustomerRole && !stillHasCustomerRole && accounts.length) {
      setMessage({
        flavor: "alert-danger",
        text: "Remove this user's assigned customer account(s) before removing their last customer role.",
      });
      return;
    }
    setMessage(null);
    setUser(withRoleDefaultMfa({ ...user, roles: newRoles }));
  }

  function onMfaChange(option) {
    setMfaPickedByHand(true);
    onChange("typeOfMFA", option.value);
  }

  function onResetTotp() {
    setConfirmResetTotp(false);
    if (resettingTotp) return;
    setResettingTotp(true);
    const failureMessage = "The authenticator app could not be reset.";
    api
      .post(`UserAdmin/ResetTotp/${user.id}`)
      .then((response) => {
        if (response?.data?.success) {
          toast.success("Authenticator app reset.");
          setUser((u) => ({ ...u, totpEnrolledAt: null }));
        } else {
          toast.error(response?.data?.message || failureMessage);
        }
      })
      .catch((err) => {
        api.catchHandler(err);
        toast.error(failureMessage);
      })
      .finally(() => setResettingTotp(false));
  }

  function totpStatusText() {
    return user.totpEnrolledAt
      ? `Authenticator app set up on ${dateHelpers.monthDayAndYear(user.totpEnrolledAt)}`
      : "Authenticator app not set up yet - they will set it up at their next sign-in.";
  }

  // A NEW user's 2FA method follows their roles until the admin picks one by hand: customers set up an
  // authenticator app, staff use Duo. An existing user's method never changes on its own.
  function withRoleDefaultMfa(changedUser) {
    if (!isNewUser || mfaPickedByHand) return changedUser;
    return {
      ...changedUser,
      typeOfMFA: anyCustomerRole(changedUser.roles) ? constants.TOTP_ID : constants.DUO_ID,
    };
  }

  function onRoleSelectChange(fieldName, selection) {
    let newRoleState = freshRoleState(
      roleSelectionState?.newRole?.typeSelection
    );
    newRoleState[fieldName] = selection;
    setRoleSelectionState({
      newRole: newRoleState,
    });
  }

  function onSave() {
    window.scroll(0, 0);
    const warnings = validate();
    if (warnings.length) {
      setMessage({
        text: warnings.join(" "),
        flavor: "alert-danger",
      });
      return;
    } else {
      setMessage(null);
    }
    // Hard stops, not confirms: neither state can be saved at all (the server rejects both). Checked before
    // the 2FA confirm so we never ask about a save that cannot proceed.
    const gate = checkRoleGate();
    if (gate) {
      setRoleGate(gate);
      return;
    }
    let payload = Object.assign({}, user);
    payload.firstName = _.trim(payload.firstName);
    payload.lastName = _.trim(payload.lastName);
    payload.email = _.trim(payload.email);
    // The Select stores the plain timezone value (option.value); reading .label off that string sent undefined, so
    // every admin save reset the user to the server default. Accept either shape, as UserProfile.js does.
    payload.timezone = payload.timezone
      ? (typeof payload.timezone === "string" ? payload.timezone : payload.timezone.label)
      : defaultTimezone?.label;
    payload.username = _.trim(payload.username);
    payload.middleName = _.trim(payload.middleName);
    payload.officeNumber = _.trim(payload.officeNumber);
    payload.faxNumber = _.trim(payload.faxNumber);
    payload.state = _.trim(payload.state);
    payload.postalCode = _.trim(payload.postalCode);
    payload.companyTitle = _.trim(payload.companyTitle);
    payload.bioLink = _.trim(payload.bioLink);
    // The full assigned set + the chosen primary. customerId (the mirror) doubles as the primary
    // designation; the server validates that it is one of customerIds.
    payload.customerIds = _.map(accounts, (a) => a.customerId);
    payload.customerId = accounts.find((a) => a.isPrimary)?.customerId ?? "";
    payload.processChangeApproverRoles = _.map(
      payload.processChangeApproverRoles,
      (p) => ({
        userId: user.id,
        typeOfProcessChangeApprover: p.value,
      })
    );
    // payload.isProductionItemApprover

    // 2FA off weakens this login to username + password only — force a conscious confirm
    // (catches accidental 2FA-off saves, e.g. the rpitts case).
    if (!payload.twoFaRequired) {
      pendingPayload.current = payload;
      setConfirm2FaOff(true);
      return;
    }
    doSave(payload);
  }

  function doSave(payload) {
    api
      .post("UserAdmin/SaveUser", payload)
      .then((response) => {
        if (response.data.success) {
          setUser({ ...user, id: response?.data?.message.id });
          navigate("/admin/users");
        } else {
          setMessage({ text: response?.data?.message, flavor: "alert-danger" });
        }
      })
      .catch(api.catchHandler);
  }

  // Mirrors the server's precedence: a mixed role list is the more fundamental problem, and it makes the
  // customer-number question moot, so it is reported alone.
  function checkRoleGate() {
    if (!hasCustomerRole) return null;
    if (hasInternalRole) return ROLE_GATE.MIXED_ROLES;
    if (!accounts.length) return ROLE_GATE.MISSING_CUSTOMER;
    return null;
  }

  function roleGateContent() {
    const who = (
      <strong>
        {user.firstName} {user.lastName}
      </strong>
    );
    if (roleGate === ROLE_GATE.MIXED_ROLES) {
      // Two ways to reach this gate with different truths: the proactive add-time block (the role was
      // just prevented, so the user still holds only one set) vs. a save of a pre-existing mixed set (the
      // user genuinely holds both). Show the message that is accurate for the situation.
      const alreadyMixed = hasCustomerRole && hasInternalRole;
      return {
        header: "Conflicting roles",
        body: alreadyMixed ? (
          <>
            {who} has both a <strong>customer role and an internal role</strong>.
            A user can have one or the other, not both.
            <br />
            <br />
            A customer role limits every screen to that customer's own data, which
            would leave the internal role's screens blank or incomplete. Remove
            whichever set of roles does not belong.
          </>
        ) : (
          <>
            A user can hold <strong>customer roles or internal roles, but not both</strong>,
            so that role was not added to {who}.
            <br />
            <br />
            A customer role limits every screen to that customer's own data, which
            would leave internal screens blank or incomplete. To switch this user's
            type, remove the roles they already have, then add the new one.
          </>
        ),
      };
    }
    return {
      header: "Customer account required",
      body: (
        <>
          {who} has a customer role, so{" "}
          <strong>at least one customer account is required</strong> before this user can be saved.
          <br />
          <br />
          A customer user is scoped to their assigned account — without one they would
          not be able to see their own orders or invoices. Assign at least one customer
          account, or remove the customer role.
        </>
      ),
    };
  }

  function validate() {
    let warnings = [];
    if (!user.firstName || _.trim(user.firstName).length < 2) {
      warnings.push("A valid first name is required.");
    }
    if (!user.lastName || _.trim(user.lastName).length < 2) {
      warnings.push("A valid last name is required.");
    }
    if (!user.email || !helpers.emailIsValid(_.trim(user.email))) {
      warnings.push("A valid email is required.");
    }
    if ((!user.password || user.password.length < 8) && user.id === 0) {
      warnings.push("Password must be at least 8 characters.");
    }
    if (!user.roles || user.roles.length === 0) {
      warnings.push("User must have at least 1 role.");
    }
    if (user.twoFaRequired && !mfaOption) {
      warnings.push(
        "Select a 2 factor authentication method or switch off the 2FA requirement."
      );
    }
    return warnings;
  }


  const fullName = user?.lastName
    ? `${user.firstName} ${user.lastName}`
    : "User";

  if (!currentUser) return null;

  return (
    <DesktopContainer screenName={`User Details`}>
      <ConfirmModal
        confirmDelete={confirm2FaOff}
        header="Save without two-factor authentication?"
        yesText="Save without 2FA"
        noText="Cancel"
        approveConfirmDelete={() => {
          setConfirm2FaOff(false);
          doSave(pendingPayload.current);
        }}
        declineConfirmDelete={() => setConfirm2FaOff(false)}
        warningMessage={
          <>
            <strong>
              {user.firstName} {user.lastName}
            </strong>{" "}
            will be able to sign in with{" "}
            <strong>only a username and password — no second factor.</strong>
            <br />
            <br />
            This is less secure and is intended only for badge-based machine-operator
            accounts. Select <em>Save without 2FA</em> to save anyway.
          </>
        }
      />
      <ConfirmModal
        confirmDelete={!!roleGate}
        acknowledgeOnly
        header={roleGateContent().header}
        yesText="OK"
        approveConfirmDelete={() => setRoleGate(null)}
        warningMessage={roleGateContent().body}
      />
      <ConfirmModal
        confirmDelete={confirmResetTotp}
        header="Reset authenticator app?"
        yesText="Reset"
        noText="Cancel"
        approveConfirmDelete={onResetTotp}
        declineConfirmDelete={() => setConfirmResetTotp(false)}
        warningMessage="They will set up their authenticator app again the next time they sign in. Use this when they have a new or lost phone."
      />
      {message ? (
        <Alert className={message.flavor}>{message.text}</Alert>
      ) : null}
      <Row className="mb-3">
        <Col xs="12">
          <Card>
            <CardBody>
              <form autoComplete="off">
                <Row className="mb-1">
                  <Col xs="3">
                    Username{" "}
                    {!user.username?.length ? helpers.requiredStar() : null}
                    <DebounceInput
                      type="text"
                      name="newUsername"
                      autoComplete="off"
                      maxLength="50"
                      onChange={(event) =>
                        onChange("username", event.target.value)
                      }
                      value={user.username || ""}
                      className="form-control"
                      debounceTimeout={300}
                    />
                  </Col>
                  {isNewUser ? (
                    <Col xs="2">
                      Password{" "}
                      {!user.password?.length ? helpers.requiredStar() : null}
                      <DebounceInput
                        type="password"
                        name="password"
                        autoComplete="off"
                        maxLength="30"
                        onChange={(event) =>
                          onChange(event.target.name, event.target.value)
                        }
                        value={user.password || ""}
                        className="form-control"
                        debounceTimeout={300}
                      />
                    </Col>
                  ) : null}
                  <Col xs={isNewUser ? 2 : 3}>
                    First Name{" "}
                    {!user.firstName?.length ? helpers.requiredStar() : null}
                    <DebounceInput
                      type="text"
                      name="firstName"
                      id="firstName"
                      maxLength="30"
                      autoFocus
                      onChange={(event) =>
                        onChange(event.target.name, event.target.value)
                      }
                      value={user.firstName || ""}
                      className="form-control"
                      debounceTimeout={300}
                    />
                  </Col>
                  <Col xs={isNewUser ? 2 : 3}>
                    Middle Name
                    <DebounceInput
                      type="text"
                      name="middleName"
                      id="middleName"
                      maxLength="30"
                      onChange={(event) =>
                        onChange(event.target.name, event.target.value)
                      }
                      value={user.middleName || ""}
                      className="form-control"
                      debounceTimeout={300}
                    />
                  </Col>
                  <Col xs={isNewUser ? 2 : 3}>
                    Last Name{" "}
                    {!user.lastName?.length ? helpers.requiredStar() : null}
                    <DebounceInput
                      type="text"
                      name="lastName"
                      id="lastName"
                      maxLength="30"
                      onChange={(event) =>
                        onChange(event.target.name, event.target.value)
                      }
                      value={user.lastName || ""}
                      className="form-control"
                      debounceTimeout={300}
                    />
                  </Col>
                </Row>
                <Row className="mb-1">
                  <Col xs="3">
                    Email {!user.email?.length ? helpers.requiredStar() : null}
                    <DebounceInput
                      type="text"
                      name="newUserEmail"
                      autoComplete="off"
                      maxLength="50"
                      onChange={(event) =>
                        onChange("email", event.target.value)
                      }
                      value={user.email || ""}
                      className="form-control"
                      debounceTimeout={300}
                    />
                  </Col>
                  <Col xs="3">
                    Mobile Phone #
                    <NumberFormat
                      className="form-control"
                      format="(###) ###-####"
                      mask="_"
                      name="mobilePhoneNumber"
                      id="mobilePhoneNumber"
                      maxLength="15"
                      onValueChange={(values) => {
                        // eslint-disable-next-line no-unused-vars
                        const { formattedValue, value } = values;
                        onChange("mobilePhoneNumber", value);
                      }}
                      value={user.mobilePhoneNumber || ""}
                      autoFocus={false}
                    />
                  </Col>
                  <Col xs="3">
                    Office Phone #
                    <NumberFormat
                      className="form-control"
                      format="(###) ###-####"
                      mask="_"
                      name="officeNumber"
                      id="officeNumber"
                      maxLength="15"
                      onValueChange={(values) => {
                        const { value } = values;
                        onChange("officeNumber", value);
                      }}
                      value={user.officeNumber || ""}
                      autoFocus={false}
                    />
                  </Col>
                  <Col xs="3">
                    Fax #
                    <NumberFormat
                      className="form-control"
                      format="(###) ###-####"
                      mask="_"
                      name="faxNumber"
                      id="faxPhoneNumber"
                      maxLength="15"
                      onValueChange={(values) => {
                        const { value } = values;
                        onChange("faxNumber", value);
                      }}
                      value={user.faxNumber || ""}
                      autoFocus={false}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col xs="3">
                    Timezone{" "}
                    {!user.timezone?.length ? helpers.requiredStar() : null}
                    <Select
                      classNamePrefix="serviceThreadSelect"
                      options={timezoneList}
                      value={
                        _.find(
                          timezoneList,
                          (x) => x.value === user.timezone
                        ) || defaultTimezone
                      }
                      onChange={(option) => onChange("timezone", option.value)}
                    />
                  </Col>
                  <Col xs="3">
                    State
                    <DebounceInput
                      type="text"
                      name="state"
                      id="state"
                      maxLength="100"
                      onChange={(event) =>
                        onChange(event.target.name, event.target.value)
                      }
                      value={user.state || ""}
                      className="form-control"
                      debounceTimeout={300}
                    />
                  </Col>
                  <Col xs="3">
                    Postal Code
                    <DebounceInput
                      type="text"
                      name="postalCode"
                      id="postalCode"
                      maxLength="50"
                      onChange={(event) =>
                        onChange(event.target.name, event.target.value)
                      }
                      value={user.postalCode || ""}
                      className="form-control"
                      debounceTimeout={300}
                    />
                  </Col>
                  <Col xs="3">
                    Country
                    <Select
                      classNamePrefix="serviceThreadSelect"
                      options={countryList}
                      value={_.find(
                        countryList,
                        (x) => x.value === user.countryId
                      )}
                      onChange={(option) => onChange("countryId", option.value)}
                    />
                  </Col>
                  <Col xs="3">
                    Company
                    <CreatableSelect
                      classNamePrefix="serviceThreadSelect"
                      onCreateOption={onCreateCompany}
                      onChange={(opt) => onChange("companyId", opt.value)}
                      options={companyList}
                      value={_.find(
                        companyList,
                        (x) => x.value === user.companyId
                      )}
                    />
                  </Col>
                  <Col xs="3">
                    Company Title
                    <DebounceInput
                      type="text"
                      name="companyTitle"
                      id="companyTitle"
                      maxLength="50"
                      onChange={(event) =>
                        onChange(event.target.name, event.target.value)
                      }
                      value={user.companyTitle || ""}
                      className="form-control"
                      debounceTimeout={300}
                    />
                  </Col>
                  <Col xs="3">
                    Bio Link
                    <DebounceInput
                      type="text"
                      name="bioLink"
                      id="bioLink"
                      maxLength="50"
                      onChange={(event) =>
                        onChange(event.target.name, event.target.value)
                      }
                      value={user.bioLink || ""}
                      className="form-control"
                      debounceTimeout={300}
                    />
                  </Col>
                  {/* <Col xs="3">Clock Number
                    <NumberFormat
                      className="form-control"
                      name="clockNumber"
                      id="clockNumber"
                      maxLength="50"
                      onValueChange={values => {
                        const { value } = values;
                        onChange("clockNumber", value);
                      }}
                      value={user.clockNumber || ""}
                      allowLeadingZeros
                    />
                  </Col> */}
                </Row>
                <Row className="mt-2">
                  {/* Customer accounts moved to their own full-width section below Role Membership
                      (multi-account list with a primary designation - step 8). */}
                  <Col xs="3">
                    Process Approval Roles
                    <Select
                      classNamePrefix="serviceThreadSelect"
                      closeMenuOnSelect={true}
                      isMulti
                      components={makeAnimated()}
                      onChange={(options) =>
                        onChange("processChangeApproverRoles", options)
                      }
                      options={processApproverRoleList}
                      value={user.processChangeApproverRoles}
                    />
                  </Col>
                  <Col xs={{ size: 2 }}>
                    <span>Prod. Item Approver</span>
                    <Switch
                      checked={user.isProductionItemApprover}
                      onChange={(event) =>
                        onChange("isProductionItemApprover", event)
                      }
                      className="float-start me-2"
                      aria-labelledby="Production Item Approver"
                    />
                  </Col>
                  <Col xs={{ size: 2 }}>
                    <span>2FA Required</span>
                    <Switch
                      checked={user.twoFaRequired}
                      onChange={(event) => onChange("twoFaRequired", event)}
                      className="float-start me-2"
                      aria-labelledby="2fa"
                    />
                  </Col>
                  {user.twoFaRequired ? (
                    <Col xs={{ size: 3 }}>
                      <span>2FA Method</span>
                      <Select
                        classNamePrefix="serviceThreadSelect"
                        options={constants.MFA_TYPES}
                        value={mfaOption}
                        onChange={onMfaChange}
                      />
                    </Col>
                  ) : null}
                  {showTotpStatus ? (
                    <Col xs={{ size: 4 }}>
                      <div>{totpStatusText()}</div>
                      <Button
                        className="projectDanger mt-1"
                        size="sm"
                        disabled={resettingTotp}
                        onClick={() => {
                          if (resettingTotp) return;
                          setConfirmResetTotp(true);
                        }}
                      >
                        Reset authenticator
                      </Button>
                    </Col>
                  ) : null}
                </Row>
              </form>
            </CardBody>
          </Card>
        </Col>
      </Row>
      <Row className="mb-3">
        <Col xs="12">
          <Card>
            <CardHeader>
              <Row>
                <Col>
                  <h5>Adjust {fullName} Role Membership</h5>
                </Col>
              </Row>
            </CardHeader>
            <CardBody>
              <Row className="pt-3">
                <Col xs="4">
                  <Label className="mb-0">Roles</Label>
                  <Select
                    classNamePrefix="serviceThreadSelect"
                    options={constants.ALL_ROLES}
                    value={roleSelectionState?.newRole?.typeSelection}
                    onChange={(option) =>
                      onRoleSelectChange("typeSelection", option)
                    }
                  />
                  {roleSelectionState && roleSelectionState.newRole ? (
                    <div
                      style={{
                        margin: "4px",
                        padding: "4px",
                        backgroundColor: "#f2f5ff",
                        borderRadius: "4px",
                      }}
                    >
                      <b>{roleSelectionState.newRole.typeSelection.label}</b>
                      {newSelectedRoleContextDescription}
                    </div>
                  ) : null}
                </Col>
                <Col className="pt-4">
                  <Button
                    className="projectSuccess"
                    onClick={onAddRole}
                    title="Add this role"
                  >
                    <FontAwesomeIcon icon="plus" />
                  </Button>
                </Col>
              </Row>
              <Row className="pt-3">
                <Col>
                  <Table size="sm">
                    <thead>
                      <tr>
                        <th>Role Name</th>
                        {/* <th>Context</th> */}
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {_.sortBy(
                        cleanseUserRoleDuplicates(user.roles),
                        (r) => constants.ROLE_DICTIONARY[r.typeOfUserRole]
                      ).map((r, index) => (
                        <tr key={`${r.typeOfUserRole}-${index}`}>
                          <td>{constants.ROLE_DICTIONARY[r.typeOfUserRole]}</td>
                          {/* <td>{r.label}</td> */}
                          <td>
                            <Button
                              onClick={() => onRemoveRole(r)}
                              className="projectDanger"
                              size="sm"
                              title="Remove"
                            >
                              <FontAwesomeIcon icon="times-circle" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>
      {/* Shown for a customer-role user, and ALSO whenever accounts exist (even without a customer role -
          e.g. mid-conversion or anomalous data) so assigned accounts can always be removed and are never
          stranded behind a hidden card. Hidden only for a user with no customer role and no accounts. */}
      {(hasCustomerRole || accounts.length > 0) && (
      <Row className="mb-3">
        <Col xs="12">
          <Card>
            <CardHeader>
              <Row>
                <Col>
                  <h5>Customer Accounts</h5>
                </Col>
              </Row>
            </CardHeader>
            <CardBody>
              <Row className="pt-3">
                <Col xs="4">
                  <Label className="mb-0">Add Account</Label>
                  <CustomerTypeahead
                    value={selectedToAdd ?? null}
                    disabled={
                      !hasCustomerRole ||
                      accounts.length >= constants.MAX_CUSTOMER_ACCOUNTS
                    }
                    placeholder={
                      !hasCustomerRole
                        ? "Add a customer role to assign accounts"
                        : accounts.length >= constants.MAX_CUSTOMER_ACCOUNTS
                        ? `Maximum ${constants.MAX_CUSTOMER_ACCOUNTS} accounts assigned`
                        : undefined
                    }
                    onChange={(e) => {
                      setSelectedToAdd(e);
                      setAccountWarning(null);
                    }}
                  />
                  {accountWarning ? (
                    <div className="small text-danger mt-1">{accountWarning}</div>
                  ) : null}
                </Col>
                <Col className="pt-4">
                  <Button
                    className="projectSuccess"
                    onClick={onAddAccount}
                    disabled={
                      !hasCustomerRole ||
                      !selectedToAdd?.value ||
                      accounts.length >= constants.MAX_CUSTOMER_ACCOUNTS
                    }
                    title="Add this account"
                  >
                    <FontAwesomeIcon icon="plus" />
                  </Button>
                </Col>
              </Row>
              <Row className="pt-3">
                <Col xs="8">
                  <Table size="sm">
                    <thead>
                      <tr>
                        <th>Account</th>
                        <th className="text-center width100px">Primary</th>
                        <th className="width100px"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {accounts.length === 0 ? (
                        <tr>
                          <td colSpan={3} className="text-muted">
                            No accounts assigned
                          </td>
                        </tr>
                      ) : (
                        accounts.map((a, index) => (
                          <tr key={`${a.customerId}-${index}`}>
                            <td>
                              {a.label}
                              {!a.isActive ? (
                                <span
                                  className="text-danger ms-2"
                                  title="This account is inactive in GP — remove it or it stays assigned"
                                >
                                  (inactive)
                                </span>
                              ) : null}
                            </td>
                            <td className="text-center">
                              {/* One primary at a time; auto-set and locked for a sole account,
                                  changeable once there are two or more. */}
                              <input
                                type="radio"
                                name="primaryAccount"
                                checked={a.isPrimary}
                                disabled={accounts.length < 2}
                                onChange={() => onSetPrimary(a.customerId)}
                                title="Set as the primary account"
                              />
                            </td>
                            <td>
                              <Button
                                onClick={() => onRemoveAccount(a.customerId)}
                                className="projectDanger"
                                size="sm"
                                title="Remove"
                              >
                                <FontAwesomeIcon icon="times-circle" />
                              </Button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>
      )}
      <Row className="my-4">
        <Col xs="12">
          <Row>
            <Col className="text-right">
              <ButtonGroup>
                <Button className="projectPrimary" onClick={onSave}>
                  Save
                </Button>
                <Button
                  className="button-secondary"
                  tag={Link}
                  to="/admin/users"
                >
                  Cancel
                </Button>
              </ButtonGroup>
            </Col>
          </Row>
        </Col>
      </Row>
    </DesktopContainer>
  );
}
